import React, { useState } from 'react';
import { generateAdventureStream } from './services/api';
import AdventureForm from './components/AdventureForm';
import TabbedView from './components/TabbedView';
import AdventureList from './components/AdventureList';
import NPCChat from './components/NPCChat';
import ProgressBar from './components/common/ProgressBar';
import ErrorDisplay from './components/common/ErrorDisplay';
import './App.css';

function App() {
    const [adventure, setAdventure] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [progress, setProgress] = useState(0); // Novo estado de progresso
    const [error, setError] = useState(null);
    const [view, setView] = useState('list'); // 'list', 'create', 'view', 'chat'

    const [simulateSleep, setSimulateSleep] = useState(false);

    const handleGenerateAdventure = async (config) => {
        setLoading(true);
        setLoadingMessage('Iniciando geração...');
        setProgress(5); // Inicia com 5%
        setError(null);
        setAdventure({}); // Start with empty object
        setView('view');

        if (simulateSleep) {
            setTimeout(() => {
                setError('Falha de conexão: O servidor na nuvem pode estar "dormindo" (ele hiberna após inatividade no plano gratuito). Aguarde 1 minuto e clique para Gerar novamente!');
                setLoading(false);
            }, 3000);
            return;
        }

        await generateAdventureStream(
            config,
            (chunk) => {
                if (chunk.type === 'progress') {
                    setLoadingMessage(chunk.message);
                    // Atualiza progresso baseado na mensagem (estimativa granular)
                    if (chunk.message.includes("Arte")) setProgress(10);
                    if (chunk.message.includes("Contexto")) setProgress(20);
                    if (chunk.message.includes("Recrutando")) setProgress(30);
                    if (chunk.message.includes("Povoando")) setProgress(40);
                    if (chunk.message.includes("Preparando")) setProgress(50);
                    if (chunk.message.includes("Ato 1")) setProgress(60);
                    if (chunk.message.includes("Ato 2")) setProgress(70);
                    if (chunk.message.includes("Ato 3")) setProgress(80);
                    if (chunk.message.includes("Ato 4")) setProgress(90);
                    if (chunk.message.includes("Ato 5")) setProgress(95);
                } else if (chunk.type === 'data') {
                    setAdventure(prev => {
                        const newData = { ...prev };
                        if (chunk.section === 'contexto' && typeof chunk.content === 'object') {
                            return { ...newData, ...chunk.content };
                        }
                        newData[chunk.section] = chunk.content;
                        return newData;
                    });

                    // Incremento suave de progresso conforme dados chegam
                    if (chunk.section === 'gerar_imagem') setProgress(15);
                    if (chunk.section === 'locais_importantes') setProgress(55); // Fim do Setup
                    if (chunk.section === 'resumo') setProgress(95); // Fim da Trama

                } else if (chunk.type === 'error') {
                    console.error("Stream error:", chunk.message);
                    setError(chunk.message);
                }
            },
            (err) => {
                console.error('Error generating adventure:', err);
                setError('Falha de conexão: O servidor na nuvem pode estar "dormindo" (ele hiberna após inatividade no plano gratuito). Aguarde 1 minuto e clique para Gerar novamente!');
                setLoading(false);
            },
            () => {
                setLoading(false);
                setLoadingMessage('');
                setProgress(100);
            }
        );
    };

    const handleSelectAdventure = (adv) => {
        setAdventure(adv.data || adv); // Handle both full DB object and flat data
        setView('view');
    };

    const handleUpdateAdventure = (section, content) => {
        setAdventure(prev => ({
            ...prev,
            [section]: content
        }));
    };

    const [theme, setTheme] = useState('dark');

    const toggleTheme = (newTheme) => {
        setTheme(newTheme);
        document.documentElement.setAttribute('data-theme', newTheme);
    };

    return (
        <div className="App">
            <header className="App-header">
                <h1>Assistente de Criação de RPG</h1>
                <div className="theme-switcher">
                    <select onChange={(e) => toggleTheme(e.target.value)} value={theme}>
                        <option value="dark">Dark</option>
                        <option value="light">Light</option>
                        <option value="fantasy">Fantasia</option>
                        <option value="cyberpunk">Cyberpunk</option>
                    </select>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '15px' }}>
                        <input
                            type="checkbox"
                            id="simulateSleep"
                            checked={simulateSleep}
                            onChange={(e) => setSimulateSleep(e.target.checked)}
                        />
                        <label htmlFor="simulateSleep" style={{ fontSize: '0.8rem', cursor: 'pointer', color: '#ff6b6b' }}>
                            🧪 Testar "Servidor Dormindo"
                        </label>
                    </div>
                </div>
                <nav>
                    <button onClick={() => setView('list')}>Minhas Aventuras</button>
                    <button onClick={() => setView('create')}>Nova Aventura</button>
                    {adventure && <button onClick={() => setView('view')}>Aventura Atual</button>}
                    {adventure && <button onClick={() => setView('chat')}>Chat com NPC</button>}
                </nav>
            </header>
            <main>
                {view === 'list' && (
                    <AdventureList
                        onSelectAdventure={handleSelectAdventure}
                        onNewAdventure={() => setView('create')}
                    />
                )}

                {view === 'create' && (
                    <>
                        <AdventureForm
                            onGenerateAdventure={handleGenerateAdventure}
                            isGenerating={loading}
                        />
                        {loading && (
                            <div className="loading-container">
                                <ProgressBar progress={progress} message={loadingMessage} />
                            </div>
                        )}
                        <ErrorDisplay message={error} />
                    </>
                )}

                {view === 'view' && adventure && (
                    <>
                        {loading && (
                            <div className="streaming-indicator">
                                <ProgressBar progress={progress} message={`Gerando: ${loadingMessage}`} />
                            </div>
                        )}
                        <TabbedView adventure={adventure} onUpdate={handleUpdateAdventure} />
                    </>
                )}

                {view === 'chat' && adventure && (
                    <NPCChat adventure={adventure} />
                )}
            </main>
        </div>
    );
}

export default App;
